import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { FC, useMemo, useState } from 'react'
import { format } from '../../../utils/date'
import Holidays from 'date-holidays'
import Tips from '../common/Tips'
import { useHistory } from 'react-router-dom'
import { setPath, setUrlId } from '../../../utils/url'
import { URL } from '../../constants/url'
import { useSelector } from 'react-redux'
import { RootState } from '../../../states/containers/store'

interface AutoLabeling {
  id: number | null
  services: {
    id: number
    name: string
  }[]
  keyword: {
    operator: string | null
    words: string[]
  }
  file_paths: string[]
  url: {
    pattern: string | null
    words: string[]
  }
  teams: {
    id: number
    name: string
  }[]
  members: {
    id: number
    name: string
  }[]
}

interface TeamWithScope {
  id: number
  name: string
  scope: string
  include_nested_teams: boolean | null
}

interface Member {
  id: number
  name: string
  kana: string | null
  email: string
  code: string | null
  profile_image: string | null
}

interface LabelGroup {
  id: number // ラベルグループID
  name: string // ラベルグループ名
  code: string | null
  color: string
  disabled: boolean // 非活性フラグ
  scheduled_start_date: string | null
  scheduled_end_date: string | null
  report_view: boolean
  permission: {
    viewable: {
      teams: TeamWithScope[]
      members: {
        id: number
        name: string
      }[]
      category_managers?: {
        id: number
        name: string
      }[]
    }
    usable: {
      teams: TeamWithScope[]
      members: {
        id: number
        name: string
      }[]
    }
    enable_settable_permission: boolean
    settable: {
      teams: TeamWithScope[]
      members: {
        id: number
        name: string
      }[]
    }
  }
  labels: { // ラベル
    id: number // ラベルID
    label_master_id: number
    name: string // ラベル名
    code: string | null
    color: string // 色
    auto_labelings: AutoLabeling[]
    disabled: boolean // 非活性フラグ
  }[]
  auto_budgets: {
    start_date: string
    end_date: string | null
    budget: number
  }[]
  auto_other_costs: {
    start_date: string
    end_date: string | null
    name: string
    cost: number
  }[]
  auto_scheduled_times: {
    start_date: string
    end_date: string | null
    time: number
  }[]
  created_at: string
  created_by: string | null
  label_group_category_ids?: number[]
}

interface Team {
  id: number
  name: string
  code: string | null
  parent_team_id: number | null
  members: {
    id: number
    name: string
    kana: string | null
    email: string
    code: string | null
    profile_image: string | null
  }[]
  managers: {
    id: number
    name: string
    kana: string | null
    email: string
    code: string | null
    profile_image: string | null
  }[]
}

interface GroupedCost {
  start_date: string
  end_date: string | null
  list: {
    name: string
    value: string
  }[]
}

interface Props {
  list: LabelGroup
  deleteLabelGroupCallback: (labelGroupId: number) => void
  disableLabelGroupCallback: (labelGroupId: number, disable: boolean) => void
  copyLabelGroupCallback: (labelGroupId: number) => void
  teams: Team[]
}

const SCOPE_LABEL: Record<string, string> = {
  both: "マネージャー・メンバー",
  manager: "マネージャー",
  member: "メンバー",
}

const LabelGroupModule: FC<Props> = (props) => {
  const {
    list,
    deleteLabelGroupCallback,
    disableLabelGroupCallback,
    copyLabelGroupCallback,
    teams,
  } = props

  const {
    login,
    task,
  } = useSelector<RootState, RootState>((state: RootState) => (state))

  const history = useHistory()

  const holidays = new Holidays()
  holidays.init('JP')

  const [isDisplay, setIsDisplay] = useState<boolean>(false)

  const invalidProjectMessage = useMemo(() => {
    const tmpMsgList:string[] = []
    if (!list.labels.filter((v) => !v.disabled).length) {
      tmpMsgList.push("業務")
    }
    if (!list.permission.usable.members.length && !list.permission.usable.teams.length) {
      tmpMsgList.push("参加チーム・ユーザー")
    }
    if (!list.permission.viewable.members.length && !list.permission.viewable.teams.length && !list.permission.viewable.category_managers?.length) {
      tmpMsgList.push("表示チーム・ユーザー")
    }
    if (tmpMsgList.length) {
      return `${tmpMsgList.join("と")}が設定されていません！`
    }
    return ""
  }, [list.labels, list.permission])

  const groupedCosts = useMemo(() => {
    let tmp: GroupedCost[] = []
    list.auto_other_costs.forEach((v) => {
      const target = tmp.find((w) => w.start_date === v.start_date && w.end_date === v.end_date)
      if (target) {
        tmp = tmp.map((w) => {
          if (w.start_date === v.start_date && w.end_date === v.end_date) {
            return { ...w, list: [...w.list, { name: v.name || "", value: v.cost.toString() }] }
          }
          return w
        })
      } else {
        tmp.push({
          start_date: v.start_date,
          end_date: v.end_date,
          list: [
            {
              name: v.name || "",
              value: v.cost.toString(),
            },
          ],
        })
      }
    })
    return tmp
  }, [])

  const getAllMemberList = (teamId: number | null): Member[] => {
    if (!teams) return []
    const tmpTeams = teams.filter((v) => v.parent_team_id === teamId)
    if (!tmpTeams.length) return []
    const tmpMembers = [...tmpTeams.map((v) => [...v.managers, ...v.members]).flat(), ...tmpTeams.map((v) => getAllMemberList(v.id).flat()).flat()]
    return Array.from(new Map(tmpMembers.map((v) => [v.id, v])).values())
  }

  const calendarColor = (day: string) => {
    const holiday = holidays.isHoliday(new Date(day))
    if ((holiday && holiday[0].type === "public") || format(day, 'd') === '0') {
      return "#FF0000"
    }
    if (format(day, 'd') === '6') {
      return "#317AB7"
    }
    return "#666666"
  }

  const dayOfWeek = (day: string | undefined) => {
    if (!day) return ""
    const holiday = holidays.isHoliday(new Date(day))
    let dd = format(day, "dd", "ja")
    if (holiday && holiday[0].type === "public") {
      dd += "・祝"
    }
    return (
      <>
        (
        <span style={{ color: calendarColor(day) }}>
          {dd}
        </span>
        )
      </>
    )
  }

  const clickCopy = (id: number) => {
    setIsDisplay(false)
    copyLabelGroupCallback(id)
  }

  const clickDetail = (id: number) => {
    history.push({
      pathname: `${setPath(setUrlId(URL.TASK_LABEL_SETTING_DETAIL.path, 'id', id), "ja")}`,
      state: { keep: true },
    })
  }

  const clickViewReport = (id: number) => {
    history.push({
      pathname: `${setPath(setUrlId(URL.TASK_DETAIL_DETAIL.path, 'id', id), "ja")}`,
    })
  }

  const buildTargetMember = (team: TeamWithScope, scope: string) => {
    const targetTeam = teams.find((v) => v.id === team.id)
    if (!targetTeam) return []
    if (scope === "manager") {
      return targetTeam.managers
    }
    if (scope === "member") {
      return targetTeam.members
    }
    return Array.from(new Set([...targetTeam.managers, ...targetTeam.members]))
  }

  return (
    <>
      <div className={`project_row ${isDisplay ? "open" : "close"}`}>
        <div
          className="project_header"
          onClick={() => setIsDisplay(!isDisplay)}
          role="none"
        >
          <div className="title" style={{ width: "100%" }}>
            <FontAwesomeIcon icon={['fas', isDisplay ? 'angle-down' : 'angle-right']} className="icon" fixedWidth />
            <div
              className="color"
              style={{
                backgroundColor: list.color,
              }}
            />
            <div className="text">
              {list.name}
              <div className="sub_area">
                <div className="sub_item">
                  <div className="item_label" style={{ backgroundColor: "#4b4c69" }}>
                    業務数
                  </div>
                  <div>
                    {`${list.labels.filter((v) => !v.disabled).length}件`}
                  </div>
                </div>
                {
                  list.code && (
                    <div className="sub_item">
                      <div className="item_label">
                        プロジェクトコード
                      </div>
                      <div>
                        {list.code}
                      </div>
                    </div>
                  )
                }
              </div>
            </div>
            {
              list.disabled && (
                <div className="completed">
                  完了済
                </div>
              )
            }
            {
              invalidProjectMessage && (
                <Tips
                  description={invalidProjectMessage}
                  className=""
                  icon={false}
                >
                  <FontAwesomeIcon
                    icon={['fas', 'exclamation-circle']}
                    style={{
                      color: "#FF0000",
                      width: "16px",
                      height: "16px",
                    }}
                  />
                </Tips>
              )
            }
          </div>
          <div className="detail">
            <div className="first">
              <div>
                <div className="title">
                  予定期間
                </div>
                <div>
                  {
                    list.scheduled_start_date ? (
                      <>
                        {format(list.scheduled_start_date, "YYYY/M/D")}
                        {dayOfWeek(list.scheduled_start_date)}
                      </>
                    ) : (
                      <>
                        未設定
                      </>
                    )
                  }
                  &nbsp;~&nbsp;
                  {
                    list.scheduled_end_date ? (
                      <>
                        {format(list.scheduled_end_date, "YYYY/M/D")}
                        {dayOfWeek(list.scheduled_end_date)}
                      </>
                    ) : (
                      <>
                        未設定
                      </>
                    )
                  }
                </div>
              </div>
              <div>
                <div className="title">
                  作成者
                </div>
                <div>
                  {list.created_by || "-"}
                </div>
              </div>
            </div>
          </div>
        </div>
        {
          isDisplay ? (
            <div className="project_detail">
              <div className="button_area">
                {
                  list.report_view && (
                    <Tips
                      description="プロジェクトのレポートを見る"
                      className=""
                      icon={false}
                    >
                      <button
                        type="button"
                        onClick={() => clickViewReport(list.id)}
                        className="view"
                      >
                        <FontAwesomeIcon icon={['fas', 'file-alt']} />
                        &nbsp;
                        レポートを見る
                      </button>
                    </Tips>
                  )
                }
                {
                  list.disabled ? (
                    <>
                      {
                        (login.role_permission?.task_project_create || login.role_permission?.task_project_setting) && (
                          <Tips
                            description="プロジェクトをコピーする"
                            className=""
                            icon={false}
                          >
                            <button
                              type="button"
                              onClick={() => clickCopy(list.id)}
                              className="copy"
                            >
                              <FontAwesomeIcon icon={['fas', 'copy']} />
                              &nbsp;
                              コピーする
                            </button>
                          </Tips>
                        )
                      }
                      <Tips
                        description="プロジェクトを未完了に戻す"
                        className=""
                        icon={false}
                      >
                        <button
                          type="button"
                          onClick={() => disableLabelGroupCallback(list.id, false)}
                        >
                          <FontAwesomeIcon icon={['fas', 'sync-alt']} />
                          &nbsp;
                          未完了に戻す
                        </button>
                      </Tips>
                    </>
                  ) : (
                    <>
                      {
                        (login.role_permission?.task_project_create || login.role_permission?.task_project_setting) && (
                          <Tips
                            description="プロジェクトをコピーする"
                            className=""
                            icon={false}
                          >
                            <button
                              type="button"
                              onClick={() => clickCopy(list.id)}
                              className="copy"
                            >
                              <FontAwesomeIcon icon={['fas', 'copy']} />
                              &nbsp;
                              コピーする
                            </button>
                          </Tips>
                        )
                      }
                      <Tips
                        description="プロジェクトを編集する"
                        className=""
                        icon={false}
                      >
                        <button
                          type="button"
                          onClick={() => clickDetail(list.id)}
                        >
                          <FontAwesomeIcon icon={['fas', 'tools']} />
                          &nbsp;
                          編集する
                        </button>
                      </Tips>
                      <Tips
                        description="プロジェクトを完了にする"
                        className=""
                        icon={false}
                      >
                        <button
                          type="button"
                          className="complete"
                          onClick={() => disableLabelGroupCallback(list.id, true)}
                        >
                          <FontAwesomeIcon icon={['fas', 'check-circle']} />
                          &nbsp;
                          完了にする
                        </button>
                      </Tips>
                      <Tips
                        description="プロジェクトを削除する"
                        className=""
                        icon={false}
                      >
                        <button
                          type="button"
                          className="delete"
                          onClick={() => deleteLabelGroupCallback(list.id)}
                        >
                          <FontAwesomeIcon icon={['fas', 'trash-alt']} />
                          &nbsp;
                          削除する
                        </button>
                      </Tips>
                    </>
                  )
                }
              </div>
              <div className="team_area">
                <div className="title">
                  {`プロジェクトレポート表示${list.permission.enable_settable_permission ? "" : "・編集権限"}対象`}
                </div>
                {
                  !!list.label_group_category_ids?.length && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        flexWrap: "wrap",
                      }}
                    >
                      {
                        task.taskLabelSettingNew?.label_group_categories
                        .filter((v) => list.label_group_category_ids?.includes(v.id)).map((v) => (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              border: "solid 1px #999999",
                              borderRadius: "20px",
                              padding: "2px 8px",
                            }}
                          >
                            <FontAwesomeIcon icon={['fas', 'folder']} style={{ color: v.color }} />
                            {v.category_name}
                          </div>
                        ))
                      }
                    </div>
                  )
                }
                {
                  !!list.permission.viewable.category_managers?.length && (
                    <div
                      style={{
                        border: "solid 1px #CCCCCC",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "8px",
                          borderBottom: "solid 1px #CCCCCC",
                          backgroundColor: "#EEEEEE",
                        }}
                      >
                        <div>
                          <Tips
                            description="プロジェクトが所属しているカテゴリーの表示ユーザーです。"
                            className=""
                          >
                            <>
                              カテゴリー表示ユーザー
                            </>
                          </Tips>
                        </div>
                      </div>
                      <div
                        className="details"
                        style={{
                          padding: "8px",
                        }}
                      >
                        {
                          list.permission.viewable.category_managers.map((member) => (
                            <div
                              className="detail"
                              key={`category_manager_${member.id}`}
                            >
                              {member.name}
                            </div>
                          ))
                        }
                      </div>
                    </div>
                  )
                }
                {
                  !!list.permission.viewable.teams?.length && (
                    <div
                      style={{
                        border: "solid 1px #CCCCCC",
                        borderRadius: "4px",
                        padding: "16px",
                        position: "relative",
                        backgroundColor: "#FFFFFF",
                        display: "flex",
                        flexFlow: "column",
                        gap: "8px",
                      }}
                    >
                      <div
                        style={{
                          backgroundColor: "#FFFFFF",
                          padding: "0 4px",
                          position: "absolute",
                          top: 0,
                          left: "8px",
                          transform: "translateY(-50%)",
                          fontSize: "10px",
                          color: "#666666",
                        }}
                      >
                        チーム
                      </div>
                      {
                        list.permission.viewable.teams.map((team) => (
                          <div
                            style={{
                              border: "solid 1px #CCCCCC",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: "8px",
                                borderBottom: "solid 1px #CCCCCC",
                                backgroundColor: "#EEEEEE",
                              }}
                            >
                              <div>
                                {`チーム「${team.name}」の${SCOPE_LABEL[team.scope]}${team.include_nested_teams ? " + 配下チーム" : ""}`}
                              </div>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                flexFlow: "column",
                                gap: "12px",
                                padding: "12px 8px 8px",
                              }}
                            >
                              {
                                ["both", "manager"].includes(team.scope) && (
                                  <div
                                    style={{
                                      border: "solid 1px #CCCCCC",
                                      borderRadius: "4px",
                                      padding: "8px",
                                      position: "relative",
                                      backgroundColor: "#FFFFFF",
                                    }}
                                  >
                                    <div
                                      style={{
                                        backgroundColor: "#FFFFFF",
                                        padding: "0 4px",
                                        position: "absolute",
                                        top: 0,
                                        left: "8px",
                                        transform: "translateY(-50%)",
                                        fontSize: "10px",
                                        color: "#666666",
                                      }}
                                    >
                                      マネージャー
                                    </div>
                                    <div
                                      className="details"
                                      style={{
                                        padding: "8px",
                                      }}
                                    >
                                      {
                                        buildTargetMember(team, "manager").map((member) => (
                                          <div
                                            className="detail"
                                            key={`member_${member.id}`}
                                          >
                                            {member.name}
                                          </div>
                                        ))
                                      }
                                    </div>
                                  </div>
                                )
                              }
                              {
                                ["both", "member"].includes(team.scope) && !team.include_nested_teams && (
                                  <div
                                    style={{
                                      border: "solid 1px #CCCCCC",
                                      borderRadius: "4px",
                                      padding: "8px",
                                      position: "relative",
                                      backgroundColor: "#FFFFFF",
                                    }}
                                  >
                                    <div
                                      style={{
                                        backgroundColor: "#FFFFFF",
                                        padding: "0 4px",
                                        position: "absolute",
                                        top: 0,
                                        left: "8px",
                                        transform: "translateY(-50%)",
                                        fontSize: "10px",
                                        color: "#666666",
                                      }}
                                    >
                                      このチームのメンバー
                                    </div>
                                    <div
                                      className="details"
                                      style={{
                                        padding: "8px",
                                      }}
                                    >
                                      {
                                        buildTargetMember(team, "member").map((member) => (
                                          <div
                                            className="detail"
                                            key={`member_${member.id}`}
                                          >
                                            {member.name}
                                          </div>
                                        ))
                                      }
                                    </div>
                                  </div>
                                )
                              }
                              {
                                ["both", "member"].includes(team.scope) && team.include_nested_teams && (
                                  <div
                                    style={{
                                      border: "solid 1px #CCCCCC",
                                      borderRadius: "4px",
                                      padding: "8px",
                                      position: "relative",
                                      backgroundColor: "#FFFFFF",
                                    }}
                                  >
                                    <div
                                      style={{
                                        backgroundColor: "#FFFFFF",
                                        padding: "0 4px",
                                        position: "absolute",
                                        top: 0,
                                        left: "8px",
                                        transform: "translateY(-50%)",
                                        fontSize: "10px",
                                        color: "#666666",
                                      }}
                                    >
                                      このチームのメンバー + 配下チームに所属している全てのマネージャー・メンバー
                                    </div>
                                    <div
                                      className="details"
                                      style={{
                                        padding: "8px",
                                      }}
                                    >
                                      {
                                        Array.from(new Map([
                                          ...getAllMemberList(team.id || null),
                                          ...buildTargetMember(team, "member"),
                                        ].map((w) => [w.id, w])).values()).sort((a, b) => a.id - b.id).map((member) => (
                                          <div
                                            className="detail"
                                            key={`member_${member.id}`}
                                          >
                                            {member.name}
                                          </div>
                                        ))
                                      }
                                    </div>
                                  </div>
                                )
                              }
                            </div>
                          </div>
                        ))
                      }
                    </div>
                  )
                }
                {
                  !!list.permission.viewable.members?.length && (
                    <>
                      <div
                        style={{
                          border: "solid 1px #CCCCCC",
                          borderRadius: "4px",
                          padding: "16px",
                          position: "relative",
                          backgroundColor: "#FFFFFF",
                        }}
                      >
                        <div
                          style={{
                            backgroundColor: "#FFFFFF",
                            padding: "0 4px",
                            position: "absolute",
                            top: 0,
                            left: "8px",
                            transform: "translateY(-50%)",
                            fontSize: "10px",
                            color: "#666666",
                          }}
                        >
                          ユーザー
                        </div>
                        <div className="details">
                          {
                            list.permission.viewable.members.map((member) => (
                              <div
                                className="detail"
                                key={`member_${member.id}`}
                              >
                                {member.name}
                              </div>
                            ))
                          }
                        </div>
                      </div>
                    </>
                  )
                }
                {
                  !list.permission.viewable.teams.length && !list.permission.viewable.members.length
                  && !list.permission.viewable.category_managers?.length && (
                    <>
                      プロジェクトレポート表示対象がいません
                    </>
                  )
                }
              </div>
              {
                list.permission.enable_settable_permission && (
                  <div className="team_area">
                    <div className="title">
                      プロジェクト編集権限対象
                    </div>
                    {
                      !!list.permission.settable.teams?.length && (
                        <div
                          style={{
                            border: "solid 1px #CCCCCC",
                            borderRadius: "4px",
                            padding: "16px",
                            position: "relative",
                            backgroundColor: "#FFFFFF",
                            display: "flex",
                            flexFlow: "column",
                            gap: "8px",
                          }}
                        >
                          <div
                            style={{
                              backgroundColor: "#FFFFFF",
                              padding: "0 4px",
                              position: "absolute",
                              top: 0,
                              left: "8px",
                              transform: "translateY(-50%)",
                              fontSize: "10px",
                              color: "#666666",
                            }}
                          >
                            チーム
                          </div>
                          {
                            list.permission.settable.teams.map((team) => (
                              <div
                                style={{
                                  border: "solid 1px #CCCCCC",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    padding: "8px",
                                    borderBottom: "solid 1px #CCCCCC",
                                    backgroundColor: "#EEEEEE",
                                  }}
                                >
                                  <div>
                                    {`チーム「${team.name}」の${SCOPE_LABEL[team.scope]}${team.include_nested_teams ? " + 配下チーム" : ""}`}
                                  </div>
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    flexFlow: "column",
                                    gap: "12px",
                                    padding: "12px 8px 8px",
                                  }}
                                >
                                  {
                                    ["both", "manager"].includes(team.scope) && (
                                      <div
                                        style={{
                                          border: "solid 1px #CCCCCC",
                                          borderRadius: "4px",
                                          padding: "8px",
                                          position: "relative",
                                          backgroundColor: "#FFFFFF",
                                        }}
                                      >
                                        <div
                                          style={{
                                            backgroundColor: "#FFFFFF",
                                            padding: "0 4px",
                                            position: "absolute",
                                            top: 0,
                                            left: "8px",
                                            transform: "translateY(-50%)",
                                            fontSize: "10px",
                                            color: "#666666",
                                          }}
                                        >
                                          マネージャー
                                        </div>
                                        <div
                                          className="details"
                                          style={{
                                            padding: "8px",
                                          }}
                                        >
                                          {
                                            buildTargetMember(team, "manager").map((member) => (
                                              <div
                                                className="detail"
                                                key={`member_${member.id}`}
                                              >
                                                {member.name}
                                              </div>
                                            ))
                                          }
                                        </div>
                                      </div>
                                    )
                                  }
                                  {
                                    ["both", "member"].includes(team.scope) && !team.include_nested_teams && (
                                      <div
                                        style={{
                                          border: "solid 1px #CCCCCC",
                                          borderRadius: "4px",
                                          padding: "8px",
                                          position: "relative",
                                          backgroundColor: "#FFFFFF",
                                        }}
                                      >
                                        <div
                                          style={{
                                            backgroundColor: "#FFFFFF",
                                            padding: "0 4px",
                                            position: "absolute",
                                            top: 0,
                                            left: "8px",
                                            transform: "translateY(-50%)",
                                            fontSize: "10px",
                                            color: "#666666",
                                          }}
                                        >
                                          このチームのメンバー
                                        </div>
                                        <div
                                          className="details"
                                          style={{
                                            padding: "8px",
                                          }}
                                        >
                                          {
                                            buildTargetMember(team, "member").map((member) => (
                                              <div
                                                className="detail"
                                                key={`member_${member.id}`}
                                              >
                                                {member.name}
                                              </div>
                                            ))
                                          }
                                        </div>
                                      </div>
                                    )
                                  }
                                  {
                                    ["both", "member"].includes(team.scope) && team.include_nested_teams && (
                                      <div
                                        style={{
                                          border: "solid 1px #CCCCCC",
                                          borderRadius: "4px",
                                          padding: "8px",
                                          position: "relative",
                                          backgroundColor: "#FFFFFF",
                                        }}
                                      >
                                        <div
                                          style={{
                                            backgroundColor: "#FFFFFF",
                                            padding: "0 4px",
                                            position: "absolute",
                                            top: 0,
                                            left: "8px",
                                            transform: "translateY(-50%)",
                                            fontSize: "10px",
                                            color: "#666666",
                                          }}
                                        >
                                          このチームのメンバー + 配下チームに所属している全てのマネージャー・メンバー
                                        </div>
                                        <div
                                          className="details"
                                          style={{
                                            padding: "8px",
                                          }}
                                        >
                                          {
                                            Array.from(new Map([
                                              ...getAllMemberList(team.id || null),
                                              ...buildTargetMember(team, "member"),
                                            ].map((w) => [w.id, w])).values()).sort((a, b) => a.id - b.id).map((member) => (
                                              <div
                                                className="detail"
                                                key={`member_${member.id}`}
                                              >
                                                {member.name}
                                              </div>
                                            ))
                                          }
                                        </div>
                                      </div>
                                    )
                                  }
                                </div>
                              </div>
                            ))
                          }
                        </div>
                      )
                    }
                    {
                      !!list.permission.settable.members?.length && (
                        <>
                          <div
                            style={{
                              border: "solid 1px #CCCCCC",
                              borderRadius: "4px",
                              padding: "16px",
                              position: "relative",
                              backgroundColor: "#FFFFFF",
                            }}
                          >
                            <div
                              style={{
                                backgroundColor: "#FFFFFF",
                                padding: "0 4px",
                                position: "absolute",
                                top: 0,
                                left: "8px",
                                transform: "translateY(-50%)",
                                fontSize: "10px",
                                color: "#666666",
                              }}
                            >
                              ユーザー
                            </div>
                            <div className="details">
                              {
                                list.permission.settable.members.map((member) => (
                                  <div
                                    className="detail"
                                    key={`member_${member.id}`}
                                  >
                                    {member.name}
                                  </div>
                                ))
                              }
                            </div>
                          </div>
                        </>
                      )
                    }
                    {
                      !list.permission.settable.teams.length && !list.permission.settable.members.length && (
                        <>
                          プロジェクト編集権限対象がいません
                        </>
                      )
                    }
                  </div>
                )
              }
              <div className="team_area">
                <div className="title">
                  プロジェクト業務仕分け対象
                </div>
                {
                  !!list.permission.usable.teams?.length && (
                    <div
                      style={{
                        border: "solid 1px #CCCCCC",
                        borderRadius: "4px",
                        padding: "16px",
                        position: "relative",
                        backgroundColor: "#FFFFFF",
                        display: "flex",
                        flexFlow: "column",
                        gap: "8px",
                      }}
                    >
                      <div
                        style={{
                          backgroundColor: "#FFFFFF",
                          padding: "0 4px",
                          position: "absolute",
                          top: 0,
                          left: "8px",
                          transform: "translateY(-50%)",
                          fontSize: "10px",
                          color: "#666666",
                        }}
                      >
                        チーム
                      </div>
                      {
                        list.permission.usable.teams.map((team) => (
                          <div
                            style={{
                              border: "solid 1px #CCCCCC",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: "8px",
                                borderBottom: "solid 1px #CCCCCC",
                                backgroundColor: "#EEEEEE",
                              }}
                            >
                              <div>
                                {`チーム「${team.name}」の${SCOPE_LABEL[team.scope]}${team.include_nested_teams ? " + 配下チーム" : ""}`}
                              </div>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                flexFlow: "column",
                                gap: "12px",
                                padding: "12px 8px 8px",
                              }}
                            >
                              {
                                ["both", "manager"].includes(team.scope) && (
                                  <div
                                    style={{
                                      border: "solid 1px #CCCCCC",
                                      borderRadius: "4px",
                                      padding: "8px",
                                      position: "relative",
                                      backgroundColor: "#FFFFFF",
                                    }}
                                  >
                                    <div
                                      style={{
                                        backgroundColor: "#FFFFFF",
                                        padding: "0 4px",
                                        position: "absolute",
                                        top: 0,
                                        left: "8px",
                                        transform: "translateY(-50%)",
                                        fontSize: "10px",
                                        color: "#666666",
                                      }}
                                    >
                                      マネージャー
                                    </div>
                                    <div
                                      className="details"
                                      style={{
                                        padding: "8px",
                                      }}
                                    >
                                      {
                                        buildTargetMember(team, "manager").map((member) => (
                                          <div
                                            className="detail"
                                            key={`member_${member.id}`}
                                          >
                                            {member.name}
                                          </div>
                                        ))
                                      }
                                    </div>
                                  </div>
                                )
                              }
                              {
                                ["both", "member"].includes(team.scope) && !team.include_nested_teams && (
                                  <div
                                    style={{
                                      border: "solid 1px #CCCCCC",
                                      borderRadius: "4px",
                                      padding: "8px",
                                      position: "relative",
                                      backgroundColor: "#FFFFFF",
                                    }}
                                  >
                                    <div
                                      style={{
                                        backgroundColor: "#FFFFFF",
                                        padding: "0 4px",
                                        position: "absolute",
                                        top: 0,
                                        left: "8px",
                                        transform: "translateY(-50%)",
                                        fontSize: "10px",
                                        color: "#666666",
                                      }}
                                    >
                                      このチームのメンバー
                                    </div>
                                    <div
                                      className="details"
                                      style={{
                                        padding: "8px",
                                      }}
                                    >
                                      {
                                        buildTargetMember(team, "member").map((member) => (
                                          <div
                                            className="detail"
                                            key={`member_${member.id}`}
                                          >
                                            {member.name}
                                          </div>
                                        ))
                                      }
                                    </div>
                                  </div>
                                )
                              }
                              {
                                ["both", "member"].includes(team.scope) && team.include_nested_teams && (
                                  <div
                                    style={{
                                      border: "solid 1px #CCCCCC",
                                      borderRadius: "4px",
                                      padding: "8px",
                                      position: "relative",
                                      backgroundColor: "#FFFFFF",
                                    }}
                                  >
                                    <div
                                      style={{
                                        backgroundColor: "#FFFFFF",
                                        padding: "0 4px",
                                        position: "absolute",
                                        top: 0,
                                        left: "8px",
                                        transform: "translateY(-50%)",
                                        fontSize: "10px",
                                        color: "#666666",
                                      }}
                                    >
                                      このチームのメンバー + 配下チームに所属している全てのマネージャー・メンバー
                                    </div>
                                    <div
                                      className="details"
                                      style={{
                                        padding: "8px",
                                      }}
                                    >
                                      {
                                        Array.from(new Map([
                                          ...getAllMemberList(team.id || null),
                                          ...buildTargetMember(team, "member"),
                                        ].map((w) => [w.id, w])).values()).sort((a, b) => a.id - b.id).map((member) => (
                                          <div
                                            className="chip"
                                            key={`member_${member.id}`}
                                          >
                                            {member.name}
                                          </div>
                                        ))
                                      }
                                    </div>
                                  </div>
                                )
                              }
                            </div>
                          </div>
                        ))
                      }
                    </div>
                  )
                }
                {
                  !!list.permission.usable.members?.length && (
                    <>
                      <div
                        style={{
                          border: "solid 1px #CCCCCC",
                          borderRadius: "4px",
                          padding: "16px",
                          position: "relative",
                          backgroundColor: "#FFFFFF",
                        }}
                      >
                        <div
                          style={{
                            backgroundColor: "#FFFFFF",
                            padding: "0 4px",
                            position: "absolute",
                            top: 0,
                            left: "8px",
                            transform: "translateY(-50%)",
                            fontSize: "10px",
                            color: "#666666",
                          }}
                        >
                          ユーザー
                        </div>
                        <div className="details">
                          {
                            list.permission.usable.members.map((member) => (
                              <div
                                className="detail"
                                key={`member_${member.id}`}
                              >
                                {member.name}
                              </div>
                            ))
                          }
                        </div>
                      </div>
                    </>
                  )
                }
                {
                  !list.permission.usable.teams.length && !list.permission.usable.members.length && (
                    <>
                      プロジェクト業務仕分け対象がいません
                    </>
                  )
                }
              </div>
              <div className="team_area">
                <div className="title">
                  業務
                </div>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                    opacity: list.disabled ? 0.5 : 1,
                  }}
                >
                  {
                    !list.labels.filter((v) => !v.disabled).length ? (
                      <div style={{ color: "#999999", fontSize: "13px" }}>
                        業務が設定されていません
                      </div>
                    ) : (
                      list.labels.filter((v) => !v.disabled).map((value) => (
                        <div
                          key={`label_${value.id}`}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            padding: "4px 10px",
                            border: "solid 1px #CCCCCC",
                            borderRadius: "16px",
                            backgroundColor: "#FFFFFF",
                            fontSize: "13px",
                          }}
                        >
                          <span
                            style={{
                              width: "12px",
                              height: "12px",
                              borderRadius: "50%",
                              backgroundColor: value.color,
                              flexShrink: 0,
                            }}
                          />
                          <span>
                            {value.name}
                          </span>
                          {
                            value.code && (
                              <span style={{ fontSize: "11px", color: "#666666" }}>
                                {`(${value.code})`}
                              </span>
                            )
                          }
                        </div>
                      ))
                    )
                  }
                </div>
              </div>
              {
                !!list.auto_budgets.length && (
                  <div className="team_area">
                    <div className="title">
                      予算
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexFlow: "column",
                        gap: "4px",
                      }}
                    >
                      {
                        list.auto_budgets.map((v) => (
                          <div
                            key={`auto_budgets_${v.start_date}`}
                            style={{
                              borderBottom: "dashed 1px #CCCCCC",
                              display: "flex",
                              padding: "4px 8px",
                              alignItems: "center",
                              justifyContent: "space-between",
                              color: "#999999",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                gap: "4px",
                              }}
                            >
                              <div>
                                {format(v.start_date, "YYYY年MM月")}
                              </div>
                              <div>
                                ~
                              </div>
                              <div>
                                {v.end_date ? format(v.end_date, "YYYY年MM月") : "指定なし"}
                              </div>
                            </div>
                            <div>
                              {`${v.budget.toLocaleString()}円`}
                            </div>
                          </div>
                        ))
                      }
                    </div>
                  </div>
                )
              }
              {
                !!groupedCosts.length && (
                  <div className="team_area">
                    <div className="title">
                      その他のコスト
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexFlow: "column",
                        gap: "16px",
                      }}
                    >
                      {
                        groupedCosts.map((v) => (
                          <div
                            key={`auto_other_costs_${v.start_date}`}
                            style={{
                              position: "relative",
                              border: "solid 1px #CCCCCC",
                              borderRadius: "4px",
                              display: "flex",
                              flexFlow: "column",
                              padding: "8px",
                              gap: "4px",
                              color: "#999999",
                            }}
                          >
                            <div
                              style={{
                                position: "absolute",
                                top: "-10px",
                                left: "8px",
                                display: "flex",
                                gap: "4px",
                                backgroundColor: "#FFFFFF",
                                padding: "2px 8px",
                              }}
                            >
                              <div>
                                {format(v.start_date, "YYYY年MM月")}
                              </div>
                              <div>
                                ~
                              </div>
                              <div>
                                {v.end_date ? format(v.end_date, "YYYY年MM月") : "指定なし"}
                              </div>
                            </div>
                            {
                              v.list.map((w) => (
                                <div
                                  key={`auto_other_costs_detail_${w.name}`}
                                  style={{
                                    borderBottom: "dashed 1px #CCCCCC",
                                    display: "flex",
                                    padding: "4px 8px",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    color: "#999999",
                                  }}
                                >
                                  <div>
                                    {w.name || "項目名なし"}
                                  </div>
                                  <div>
                                    {`${Number(w.value || 0).toLocaleString()}円`}
                                  </div>
                                </div>
                              ))
                            }
                          </div>
                        ))
                      }
                    </div>
                  </div>
                )
              }
              {
                !!list.auto_scheduled_times.length && (
                  <div className="team_area">
                    <div className="title">
                      予定時間
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexFlow: "column",
                        gap: "4px",
                      }}
                    >
                      {
                        list.auto_scheduled_times.map((v) => (
                          <div
                            key={`auto_scheduled_times_${v.start_date}`}
                            style={{
                              borderBottom: "dashed 1px #CCCCCC",
                              display: "flex",
                              padding: "4px 8px",
                              alignItems: "center",
                              justifyContent: "space-between",
                              color: "#999999",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                gap: "4px",
                              }}
                            >
                              <div>
                                {format(v.start_date, "YYYY年MM月")}
                              </div>
                              <div>
                                ~
                              </div>
                              <div>
                                {v.end_date ? format(v.end_date, "YYYY年MM月") : "指定なし"}
                              </div>
                            </div>
                            <div>
                              {`${Math.floor(v.time / 3600)}時間`}
                              {
                                v.time % 3600 ? (
                                  <>
                                    {`${(v.time % 3600) / 60}分`}
                                  </>
                                ) : (
                                  <></>
                                )
                              }
                            </div>
                          </div>
                        ))
                      }
                    </div>
                  </div>
                )
              }
            </div>
          ) : (
            <></>
          )
        }
      </div>
    </>
  )
}

export default LabelGroupModule
